import React from 'react';
import { Message, Icon } from 'semantic-ui-react';
import BaseModal, { type BaseModalAction } from '../shared/BaseModal';
import type { BaseModalTab } from '../shared/BaseModal/types';

/** Abas do modal de criação/edição de contrato (ordem fixa) */
export const CONTRACT_FORM_TABS: BaseModalTab[] = [
  { key: 'cliente', label: 'Cliente', icon: 'user' },
  // 'dadosTim' só aparece quando a consulta por CNPJ encontra dados na base TIM (ver ContractsPage.tsx).
  { key: 'dadosTim', label: 'Dados TIM', icon: 'database' },
  { key: 'contrato', label: 'Contrato', icon: 'file alternate' },
  { key: 'linhas', label: 'Linhas', icon: 'list' },
  { key: 'linhasDesativar', label: 'Linhas a Desativar', icon: 'ban' },
  { key: 'meuTim', label: 'Meu TIM', icon: 'mobile alternate' },
  { key: 'endereco', label: 'Endereço', icon: 'map marker alternate' },
  { key: 'administrador', label: 'Responsável pela Auditoria', icon: 'user secret' },
  { key: 'representantes', label: 'Representante Legal QSA', icon: 'users' },
  { key: 'recebedores', label: 'Recebedores', icon: 'user check' },
  { key: 'testemunhas', label: 'Testemunhas', icon: 'eye' },
  { key: 'observacoesInternas', label: 'Observações Internas', icon: 'sticky note outline' },
];

export const CONTRACT_LINES_ONLY_TAB_KEYS = ['linhas', 'linhasDesativar'] as const;

export const CONTRACT_LINES_ONLY_TABS: BaseModalTab[] = CONTRACT_FORM_TABS.filter((tab) =>
  (CONTRACT_LINES_ONLY_TAB_KEYS as readonly string[]).includes(tab.key)
);

export interface ContractFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  activeTab: string;
  onTabChange: (tabKey: string) => void;
  actions: BaseModalAction[];
  tabs?: BaseModalTab[];
  tabValidationError?: string;
  duplicatedFromContractId?: string | null;
  children: React.ReactNode;
}

/**
 * Modal de criação/edição de contrato.
 * Encapsula BaseModal com as abas fixas do fluxo, mensagens de validação e banner de duplicação.
 * O conteúdo das abas (renderTabContent) e as ações (botões Anterior/Próximo/Salvar) vêm da página.
 */
export const ContractFormModal: React.FC<ContractFormModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  activeTab,
  onTabChange,
  actions,
  tabs,
  tabValidationError,
  duplicatedFromContractId,
  children,
}) => {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      icon="file alternate"
      size="large"
      tabs={tabs ?? CONTRACT_FORM_TABS}
      activeTab={activeTab}
      onTabChange={onTabChange}
      actions={actions}
      closeOnDimmerClick={false}
      closeIcon
    >
      {tabValidationError && (
        <Message
          error
          style={{
            marginBottom: '15px',
            position: 'sticky',
            top: 0,
            zIndex: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
        >
          <Icon name="exclamation triangle" />
          <strong>Erro de validação:</strong> {tabValidationError}
        </Message>
      )}
      {duplicatedFromContractId && (
        <Message info style={{ marginBottom: '20px', border: '2px solid #2185d0' }}>
          <Icon name="copy" />
          <Message.Header>Novo Contrato Baseado em #{duplicatedFromContractId}</Message.Header>
          <p>
            Você está criando um novo contrato baseado no contrato #{duplicatedFromContractId}. Revise todos os dados
            antes de salvar. ICCIDs não foram copiados e serão buscados novos.
          </p>
        </Message>
      )}
      <div id="gettel-contract-form-modal-body">{children}</div>
    </BaseModal>
  );
};
